import Debugs from "../Debugs.js";
import AbstractIntersectionHandler from "./AbstractIntersectionHandler.js";

//statische Hilfsvariablen, die in isIntersectingTriangle benutzt werden,
//aber aus Performancegründen nicht bei jedem Methodenaufruf neu erzeugt werden
const triangle_v = [null, null, null];

/**
 * Ermittelt alle Schnittpolygone von Meshes mit einer unendlichen Ebene.
 * http://preview.objectcode.de/HoloExamples/#oc-threejs.TestIntersectionHandler
 * @example
 * // Die Ergebnisse liegen in der folgenden Form vor:
 * {
 *      numIntersections, // Anzahl Schnittpunkte mit dem Dreieck (1-3)
 *      isInterior, // true oder false
 *      point, // Array der Schnittpunkte des Schnittpolygons
 *      object, // das Mesh-Objekt
 *      face, // das Dreieck
 *      faceIndex, // Index des Dreiecks
 * }
 * // Typischer Aufruf:
 * var handler = Utils.PlaneIntersectionHandler(objects,{plane: Maths3.plane.y0});
 * handler.intersect(intersection => console.log(intersection));
 * // Ergebnis testweise visualisieren
 * handler.debugShow(true);
 */
export default class PlaneIntersectionHandler extends AbstractIntersectionHandler {

    constructor(objects,options) {
        super(objects,options);

        this.planeInWorldSpace = (options && options.plane) || Math3.plane.y0;
        this.plane = this.planeInWorldSpace.clone();
    }


    debugShow (showPlane = false) {
        if (showPlane) {
            Debugs.plane(this.planeInWorldSpace, 0xa0a0a0);
        }
        this.intersect(x => {
            x.point.forEach(p=>Debugs.point(p));
            Debugs.line(x.point);
        });
    }


    isIntersectionSphere (sphere) {
        return this.planeInWorldSpace.distanceToSphere(sphere) <= 0;
    }

    getGeometryInLocalSpace () {
        return this.plane;
    }

    getGeometryInWorldSpace () {
        return this.planeInWorldSpace;
    }

    intersectTriangle (a, b, c, backfaceCulling, worldMatrix, pushIntersectionCallback) {

        // from http://www.geometrictools.com/GTEngine/Include/Mathematics/GteIntrPlane3Triangle3.h

        // Determine on which side of the this.plane the vertices lie.  The table of
        // possibilities is listed next with n = numNegative, p = numPositive, and
        // z = numZero.
        //
        //   n p z  intersection
        //   ------------------------------------
        //   0 3 0  none
        //   0 2 1  vertex
        //   0 1 2  edge
        //   0 0 3  triangle in the this.plane
        //   1 2 0  segment (2 edges clipped)
        //   1 1 1  segment (1 edge clipped)
        //   1 0 2  edge
        //   2 1 0  segment (2 edges clipped)
        //   2 0 1  vertex
        //   3 0 0  none

        triangle_v[0] = a;
        triangle_v[1] = b;
        triangle_v[2] = c;

        //triangle_v = this.applyMatrixOnPoints2(worldMatrix,triangle_v);

        const s = [0, 0, 0];

        s[0] = 0;
        s[1] = 0;
        s[2] = 0;

        let numPositive = 0;
        let numNegative = 0;
        let numZero = 0;

        for (let i = 0; i < 3; ++i) {
            // bei ThreeJS ist this.plane.constant * -1 im Vergleich zu GTEngine! (ThreeJS verwendet die übliche hessische Normalform)
            s[i] = this.plane.normal.dot(triangle_v[i]) + this.plane.constant;
            if (s[i] > 0) {
                ++numPositive;
            }
            else if (s[i] < 0) {
                ++numNegative;
            }
            else {
                ++numZero;
            }
        }

        if (numZero === 0 && numPositive > 0 && numNegative > 0) {
            const sign = 3 - numPositive * 2;
            for (var i0 = 0; i0 < 3; ++i0) {
                if (sign * s[i0] > 0) {
                    var i1 = (i0 + 1) % 3;
                    var i2 = (i0 + 2) % 3;
                    const t1 = s[i1] / (s[i1] - s[i0]);
                    const t2 = s[i2] / (s[i2] - s[i0]);
                    pushIntersectionCallback({
                        numIntersections: 2,
                        isInterior: true,
                        point: this.applyMatrixOnPoints(worldMatrix, [
                            // triangle.v[i1] + t1 * (triangle.v[i0] - triangle.v[i1]);
                            triangle_v[i0].clone().sub(triangle_v[i1]).multiplyScalar(t1).add(triangle_v[i1]),
                            triangle_v[i0].clone().sub(triangle_v[i2]).multiplyScalar(t2).add(triangle_v[i2]),
                        ]),
                    });
                    return;
                }
            }
        }

        if (numZero == 1) {
            for (let i0 = 0; i0 < 3; ++i0) {
                if (s[i0] === 0) {
                    var i1 = (i0 + 1) % 3;
                    var i2 = (i0 + 2) % 3;
                    if (numPositive == 2 || numNegative == 2) {
                        pushIntersectionCallback({
                            numIntersections: 1,
                            isInterior: false,
                            point: this.applyMatrixOnPoints(worldMatrix, [triangle_v[i0].clone()]),
                        });
                        return;
                    } else {
                        const t = s[i1] / (s[i1] - s[i2]);
                        pushIntersectionCallback({
                            numIntersections: 2,
                            isInterior: true,
                            point: this.applyMatrixOnPoints(worldMatrix, [
                                triangle_v[i0].clone(),
                                triangle_v[i2].clone().sub(triangle_v[i1]).multiplyScalar(t).add(triangle_v[i1]),
                            ]),
                        });
                        return;
                    }
                }
            }
        }

        if (numZero == 2) {
            for (let i0 = 0; i0 < 3; ++i0) {
                if (s[i0] !== 0) {
                    var i1 = (i0 + 1) % 3;
                    var i2 = (i0 + 2) % 3;
                    pushIntersectionCallback({
                        numIntersections: 2,
                        isInterior: false,
                        point: this.applyMatrixOnPoints(worldMatrix, [triangle_v[i1].clone(), triangle_v[i2].clone()]),
                    });
                    return;
                }
            }
        }

        if (numZero == 3) {
            pushIntersectionCallback({
                numIntersections: 3,
                isInterior: false,
                point: this.applyMatrixOnPoints(worldMatrix, triangle_v.map(p=>p.clone())),
            });
            return;
        }
    }

}
